/* mo-cmp-cut.js: the cut showpiece of the comparison articles (snippets/mo-cmp-cut.liquid), Mattress Overstock,
   2026-10-15.
   Top row: one bed from each brand cut open on one floor line at one scale (px per inch), each layer at the depth the
   maker publishes; a bed whose maker publishes no depths is drawn at its true height with "Layer depths not published".
   Bottom row: a close-up of each support core (not to scale): pocketed coils move one by one, a coil in a coil adds its
   inner coil once the outer one has shortened past the gap, a foam core dips as one piece. "Press both" puts the same
   load on both rows. It shows the idea, not a measurement.
   The same svg() runs in Node to write the server still (build_still.js), so the live drawing lands on the still.
   Plain JS, no library. Animates only while a press is moving and the figure is on screen. */
(function () {
  'use strict';

  var KIND = { cover: 'cv', mem: 'mm', foam: 'fm', support: 'sp', zone: 'zn', core: 'co', base: 'bs' };
  var WIRE = ' fill="none" stroke="#dde1e5" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"';
  var WIRE_T3 = ' fill="none" stroke="#dde1e5" stroke-width=".75" stroke-linejoin="round" stroke-linecap="round"';
  var HB = ' fill="none" stroke="#6f747a" stroke-linecap="round" stroke-linejoin="round"';
  var HF = ' fill="none" stroke="#e6e9ec" stroke-linecap="round" stroke-linejoin="round"';
  var WORD = { cover: 'Cover', mem: 'Memory foam', foam: 'Comfort foam', support: 'Support foam', zone: 'Zoned foam', core: 'Foam core', base: 'Base foam' };

  function G(narrow) {
    if (narrow) return { W: 360, s: 10.5, floor: 248, top: 4, ax: 60, bx: 202, w: 98, gx: 181, cy: 268, ch: 90, lab: 52, n: 4, f1: 13.5, f2: 12, bo: 6 };
    return { W: 640, s: 12, floor: 272, top: 4, ax: 92, bx: 352, w: 196, gx: 320, cy: 290, ch: 92, lab: 84, n: 5, f1: 14, f2: 12, bo: 9 };
  }

  function esc(t) { return String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function r1(v) { return Math.round(v * 10) / 10; }
  function num(v) { return (Math.round(v * 100) / 100).toString(); }

  // a side view of a coil: a zigzag of n turns between y0 and y1, centered on cx, half width hw
  function zig(cx, y0, y1, hw, n) {
    var pts = [], steps = n * 2, i, y, x;
    for (i = 0; i <= steps; i++) {
      y = y0 + (y1 - y0) * i / steps;
      x = (i === 0 || i === steps) ? cx : cx + (i % 2 ? hw : -hw);
      pts.push(r1(x) + ',' + r1(y));
    }
    return pts.join(' ');
  }

  // a side view of a helix of n turns: returns [back path, front path]
  function helix(cx, y0, y1, hw, n, hw2) {
    var per = 10, steps = n * per, back = '', front = '', prev = null, i, t, x, y, f, seg, r;
    for (i = 0; i <= steps; i++) {
      t = i / steps * n * Math.PI * 2;
      r = hw2 == null ? hw : hw + (hw2 - hw) * i / steps;     // a cone when the bottom radius differs
      x = cx + r * Math.sin(t);
      y = y0 + (y1 - y0) * i / steps + Math.cos(t) * r * 0.16;
      f = Math.cos(t) >= 0;
      seg = r1(x) + ' ' + r1(y);
      if (prev && prev.f === f) { if (f) front += ' L' + seg; else back += ' L' + seg; }
      else {
        if (prev) { if (f) front += ' M' + prev.p + ' L' + seg; else back += ' M' + prev.p + ' L' + seg; }
        else { if (f) front += 'M' + seg; else back += 'M' + seg; }
      }
      prev = { f: f, p: seg };
    }
    return [back, front];
  }

  function coreLabel(bed, narrow) {
    var c = bed.core;
    if (c === 'foam-zone') return narrow ? 'support' : 'support foam';
    return 'coils';
  }

  // geometry of one bed's cut: bands from the floor up
  function bands(bed, g) {
    var L = bed.layers || [], known = 0, i, d, out = [], y = g.floor, anyDepth = false, coreTop = null, coreBot = null;
    for (i = 0; i < L.length; i++) if (L[i][1] != null && L[i][2] !== 'cover') { known += L[i][1]; anyDepth = true; }
    if (!anyDepth) return { none: true, top: g.floor - bed.h * g.s };
    for (i = L.length - 1; i >= 0; i--) {
      d = L[i][1];
      if (d == null) continue;
      var h = d * g.s;
      out.push({ n: i + 1, kind: L[i][2], name: L[i][0], d: d, y: y - h, h: h });
      if ((L[i][2] === 'core' || L[i][2] === 'zone') && coreBot == null) { coreBot = y; coreTop = y - h; }
      else if (L[i][2] === 'core' || L[i][2] === 'zone') { coreTop = y - h; }
      y -= h;
    }
    var top = Math.min(y, g.floor - bed.h * g.s);
    // where the published depths stop short of the height, the rest is the cover and quilting, depth not published
    if (g.floor - bed.h * g.s < y - 0.5) out.push({ n: 1, kind: 'gap', name: 'Cover and quilting', d: null, y: top, h: y - top });
    return { none: false, top: top, list: out, coreTop: coreTop, coreBot: coreBot };
  }

  function bedSvg(bed, x, g, side, brand) {
    var b = bands(bed, g), o = [], i, k, cx, narrow = g.W < 400;
    var mid = x + g.w / 2, ty = Math.min(b.top, g.floor - bed.h * g.s);
    o.push('<g class="ck-bed ck-bed--' + side + '" data-bed>');
    if (narrow) {
      o.push('<text class="ck-t1" x="' + mid + '" y="' + r1(ty - 38) + '" text-anchor="middle">' + esc(brand) + '</text>');
      o.push('<text class="ck-t2" x="' + mid + '" y="' + r1(ty - 23) + '" text-anchor="middle">' + esc(bed.short || bed.name) + '</text>');
      o.push('<text class="ck-t2 ck-t2b" x="' + mid + '" y="' + r1(ty - 8) + '" text-anchor="middle">' + num(bed.h) + ' in.</text>');
    } else {
      o.push('<text class="ck-t1" x="' + mid + '" y="' + r1(ty - 26) + '" text-anchor="middle">' + esc(brand) + ' <tspan class="ck-t1b">' + num(bed.h) + ' in.</tspan></text>');
      o.push('<text class="ck-t2" x="' + mid + '" y="' + r1(ty - 10) + '" text-anchor="middle">' + esc(bed.name) + '</text>');
    }
    if (b.none) {
      var hh = bed.h * g.s, L = bed.layers || [], lines = [];
      o.push('<rect class="ck-none" x="' + x + '" y="' + r1(ty) + '" width="' + g.w + '" height="' + r1(hh) + '" rx="3"/>');
      o.push('<rect class="ck-hatch" x="' + x + '" y="' + r1(ty) + '" width="' + g.w + '" height="' + r1(hh) + '" rx="3"/>');
      if (!narrow) for (i = 0; i < L.length; i++) {
        var nm = String(L[i][0]).split(' (')[0];
        if (nm.length > 30) nm = WORD[L[i][2]] || nm;
        lines.push((i + 1) + '. ' + nm);
      }
      var lh = narrow ? 14 : 16, y0 = ty + hh / 2 - ((lines.length ? lines.length + 1.6 : 1) * lh) / 2 + lh * 0.7;
      o.push('<text class="ck-np" x="' + mid + '" y="' + r1(y0) + '" text-anchor="middle">' + (narrow ? 'Depths' : 'Layer depths not published') + '</text>');
      if (narrow) o.push('<text class="ck-np" x="' + mid + '" y="' + r1(y0 + lh) + '" text-anchor="middle">not published</text>');
      for (i = 0; i < lines.length; i++) o.push('<text class="ck-nl" x="' + mid + '" y="' + r1(y0 + lh * (i + 1.6)) + '" text-anchor="middle">' + esc(lines[i]) + '</text>');
      o.push('<rect class="ck-edge" x="' + x + '" y="' + r1(ty) + '" width="' + g.w + '" height="' + r1(hh) + '" rx="3"/>');
      o.push('</g>');
      return { svg: o.join(''), b: b };
    }
    for (i = 0; i < b.list.length; i++) {
      k = b.list[i];
      var cls = k.kind === 'gap' ? 'ck-gap' : (k.kind === 'core' && bed.core === 'foam-zone' ? 'ck-fc' : 'ck-' + (KIND[k.kind] || 'fm'));
      o.push('<rect class="ck-band ' + cls + '" style="--i:' + i + '" x="' + x + '" y="' + r1(k.y) + '" width="' + g.w + '" height="' + r1(k.h) + '"/>');
      if (k.kind === 'core' && bed.core !== 'foam-zone') {
        // one coil drawn once, repeated with <use> (keeps the server still small)
        var pitch = 2 * g.s, nC = Math.floor(g.w / pitch), off = (g.w - nC * pitch) / 2, turns = Math.max(3, Math.round(k.d / 1.15));
        var cid = 'ckc-' + (narrow ? 'n' : 'w') + side;
        o.push('<defs><g id="' + cid + '"><rect class="ck-pocket" fill="none" stroke="#4c4844" stroke-width="1" x="' + r1(-pitch / 2 + 1) + '" y="' + r1(k.y + 1.5) + '" width="' + r1(pitch - 2) + '" height="' + r1(k.h - 3) + '" rx="' + r1(pitch / 3) + '"/>');
        if (bed.core === 't3') for (var st = -1; st <= 1; st++) o.push('<polyline class="ck-wire ck-wire--t3"' + WIRE_T3 + ' points="' + zig(st * 1.3, k.y + 4, k.y + k.h - 4, pitch * 0.32, turns) + '"/>');
        else o.push('<polyline class="ck-wire"' + WIRE + ' points="' + zig(0, k.y + 4, k.y + k.h - 4, pitch * 0.34, turns) + '"/>');
        o.push('</g></defs>');
        for (cx = 0; cx < nC; cx++) o.push('<use href="#' + cid + '" x="' + r1(x + off + pitch * cx + pitch / 2) + '"/>');
      }
      if (k.kind === 'core' && bed.core === 'foam-zone') o.push('<rect class="ck-dots" x="' + x + '" y="' + r1(k.y) + '" width="' + g.w + '" height="' + r1(k.h) + '"/>');
      if (k.kind === 'zone') o.push('<rect class="ck-zone" x="' + x + '" y="' + r1(k.y) + '" width="' + g.w + '" height="' + r1(k.h) + '"/>');
      if (k.kind === 'gap') o.push('<rect class="ck-hatch" x="' + x + '" y="' + r1(k.y) + '" width="' + g.w + '" height="' + r1(k.h) + '"/>');
      // the layer's own name inside the band, where it fits (wide drawing only; the list below has every layer)
      if (!narrow && k.h >= 17 && !(k.kind === 'core' && bed.core !== 'foam-zone') && k.kind !== 'gap') {
        o.push('<text class="ck-ln' + (k.kind === 'base' ? ' ck-ln--d' : '') + '" x="' + (x + 8) + '" y="' + r1(k.y + k.h / 2 + 4) + '">' + esc(/latex/i.test(k.name) ? 'Latex' : (WORD[k.kind] || 'Foam')) + ', ' + num(k.d) + ' in.</text>');
      }
    }
    o.push('<path class="ck-quilt" d="M' + x + ' ' + r1(b.top) + ' h' + g.w + '"/>');
    o.push('<rect class="ck-edge" x="' + x + '" y="' + r1(b.top) + '" width="' + g.w + '" height="' + r1(g.floor - b.top) + '" rx="3"/>');
    o.push('</g>');
    return { svg: o.join(''), b: b };
  }

  // red bracket for what sits on top of the support, light bracket for the support
  function brackets(bed, b, x, g, side) {
    if (b.none || b.coreTop == null) return '';
    var o = [], out = side === 'a' ? -1 : 1, bx = side === 'a' ? x - g.bo : x + g.w + g.bo, tx = bx + out * 5;
    var anc = side === 'a' ? 'end' : 'start';
    var topIn = (b.coreTop - b.top) / g.s, coreIn = (b.coreBot - b.coreTop) / g.s;
    function br(y0, y1, cls) {
      return '<path class="' + cls + '" d="M' + r1(bx - out * 4) + ' ' + r1(y0 + 1) + ' H' + r1(bx) + ' V' + r1(y1 - 1) + ' H' + r1(bx - out * 4) + '"/>';
    }
    o.push(br(b.top, b.coreTop, 'ck-br ck-br--top'));
    var my = (b.top + b.coreTop) / 2;
    o.push('<text class="ck-bn ck-bn--top" x="' + r1(tx) + '" y="' + r1(my - 1) + '" text-anchor="' + anc + '">' + num(topIn) + ' in.</text>');
    o.push('<text class="ck-bl" x="' + r1(tx) + '" y="' + r1(my + g.f2 + 1) + '" text-anchor="' + anc + '">on top</text>');
    o.push(br(b.coreTop, b.coreBot, 'ck-br'));
    my = (b.coreTop + b.coreBot) / 2;
    o.push('<text class="ck-bn" x="' + r1(tx) + '" y="' + r1(my - 1) + '" text-anchor="' + anc + '">' + num(coreIn) + ' in.</text>');
    o.push('<text class="ck-bl" x="' + r1(tx) + '" y="' + r1(my + g.f2 + 1) + '" text-anchor="' + anc + '">' + coreLabel(bed, g.W < 400) + '</text>');
    return o.join('');
  }

  function ruler(g, maxIn) {
    var o = [], i, y, top = Math.ceil(maxIn);
    o.push('<line class="ck-axis" x1="' + g.gx + '" y1="' + g.floor + '" x2="' + g.gx + '" y2="' + r1(g.floor - top * g.s) + '"/>');
    for (i = 0; i <= top; i++) {
      y = r1(g.floor - i * g.s);
      o.push('<line class="ck-tick' + (i % 4 ? '' : ' ck-tick--big') + '" x1="' + (g.gx - (i % 4 ? 3 : 6)) + '" y1="' + y + '" x2="' + (g.gx + (i % 4 ? 3 : 6)) + '" y2="' + y + '"/>');
      if (i % 4 === 0 && i > 0) {
        o.push('<line class="ck-grid" x1="' + (g.ax - 2) + '" y1="' + y + '" x2="' + (g.bx + g.w + 2) + '" y2="' + y + '"/>');
        if (i <= 12) o.push('<text class="ck-tl" x="' + (g.gx + (g.W < 400 ? 0 : 9)) + '" y="' + r1(g.W < 400 ? y - 5 : y + 4) + '"' + (g.W < 400 ? ' text-anchor="middle"' : '') + '>' + i + '</text>');
      }
    }
    o.push('<text class="ck-tl" x="' + g.gx + '" y="' + r1(g.floor + 14) + '" text-anchor="middle">in.</text>');
    o.push('<line class="ck-floor" x1="' + (g.ax - g.lab + 8) + '" y1="' + g.floor + '" x2="' + (g.bx + g.w + g.lab - 8) + '" y2="' + g.floor + '"/>');
    return o.join('');
  }

  // close-up row: one row of the support core, not to scale. Each column has an id for the press.
  function cuGeom(g, side) {
    var x = side === 'a' ? g.ax : g.bx, pitch = g.w / g.n, y0 = g.cy + 22, h = g.ch - 22;
    return { x: x, pitch: pitch, y0: y0, h: h, top: y0 + 12, bot: y0 + h - 2 };
  }

  function coilDs(core, cx, top, bot, pitch, inTop) {
    var hw = pitch * 0.34, out = [], inn = null;
    // IntelliCoil: a cone-shaped inner coil nested in the tall outer coil, starting below its top (maker's story and picture)
    if (core === 'coil-in-coil') inn = helix(cx, Math.max(top, inTop), bot, hw * 0.3, 4, hw * 0.56);
    out.push(helix(cx, top, bot, core === 't3' ? hw * 0.92 : hw, 6));
    return { out: out, inn: inn };
  }

  function coilPaths(core, cx, top, bot, pitch, inTop, id) {
    var d = coilDs(core, cx, top, bot, pitch, inTop), o = '';
    if (d.inn) o += '<path class="ck-hb ck-h--in" fill="none" stroke="#7c6440" stroke-width="1.4" data-inb d="' + d.inn[0] + '"/><path class="ck-hf ck-h--in" fill="none" stroke="#d6ae6c" stroke-width="1.7" data-in d="' + d.inn[1] + '"/>';
    if (core === 't3') {
      // three strands wound into one coil: one strand drawn, two copies beside it follow it
      o += '<g id="' + id + '"><path class="ck-hb ck-h--t3"' + HB + ' stroke-width=".8" data-ob d="' + d.out[0][0] + '"/><path class="ck-hf ck-h--t3"' + HF + ' stroke-width="1" data-of d="' + d.out[0][1] + '"/></g>';
      o += '<use href="#' + id + '" x="-1.4"/><use href="#' + id + '" x="1.4"/>';
      return o;
    }
    d.out.forEach(function (p) { o += '<path class="ck-hb"' + HB + ' stroke-width="1.4" data-ob d="' + p[0] + '"/><path class="ck-hf"' + HF + ' stroke-width="1.9" data-of d="' + p[1] + '"/>'; });
    return o;
  }

  function closeup(bed, g, side, compact) {
    var c = cuGeom(g, side), o = [], i, cx, inTop = c.top + (c.bot - c.top) * 0.3, tag = (g.W < 400 ? 'n' : 'w') + side;
    o.push('<g class="ck-cu ck-cu--' + side + '" data-cu="' + side + '" data-core="' + esc(bed.core) + '">');
    if (bed.core === 'foam-zone') {
      o.push('<path class="ck-cu__foam" d="' + foamPath(c.x, c.y0 + 10, g.w, c.h - 10, null) + '" data-cu-foam/>');
      for (i = 0; i < 5; i++) {
        var zx = c.x + g.w * i / 5;
        o.push('<path class="ck-cu__zone ck-cu__zone--' + (i % 2 ? 'f' : 'p') + '" d="' + bandPath(zx, g.w / 5, function () { return c.y0 + 10; }, function () { return c.y0 + 24; }) + '" data-cu-zone="' + i + '"/>');
      }
    } else {
      if (compact) {
        // the server still: one resting column, repeated (the script draws every column itself)
        o.push('<defs><g id="cuc-' + tag + '"><rect class="ck-pocket" fill="none" stroke="#4c4844" stroke-width="1" x="' + r1(-c.pitch / 2 + 1.5) + '" y="' + (c.y0 + 11) + '" width="' + r1(c.pitch - 3) + '" height="' + (c.h - 12) + '" rx="' + r1(c.pitch / 3) + '"/>');
        o.push(coilPaths(bed.core, 0, c.top, c.bot, c.pitch, inTop, 'cks-' + tag) + '</g></defs>');
        for (i = 0; i < g.n; i++) o.push('<use href="#cuc-' + tag + '" x="' + r1(c.x + c.pitch * i + c.pitch / 2) + '"/>');
      }
      for (i = 0; i < g.n && !compact; i++) {
        cx = c.x + c.pitch * i + c.pitch / 2;
        o.push('<g data-cu-col="' + i + '">');
        o.push('<rect class="ck-pocket" x="' + r1(cx - c.pitch / 2 + 1.5) + '" y="' + (c.y0 + 11) + '" width="' + r1(c.pitch - 3) + '" height="' + (c.h - 12) + '" rx="' + r1(c.pitch / 3) + '"/>');
        o.push(coilPaths(bed.core, cx, c.top, c.bot, c.pitch, inTop, 'cku-' + (g.W < 400 ? 'n' : 'w') + side + i));
        o.push('</g>');
      }
    }
    o.push('<path class="ck-cu__top" d="' + bandPath(c.x, g.w, function () { return c.y0; }, function () { return c.y0 + 10; }) + '" data-cu-top/>');
    o.push('<circle class="ck-puck" cx="' + r1(c.x + g.w / 2) + '" cy="' + (c.y0 - 13) + '" r="12" data-puck/>');
    o.push('</g>');
    return o.join('');
  }

  function bandPath(x, w, up, lo) {
    var p = [], i, steps = 28, px;
    for (i = 0; i <= steps; i++) { px = x + w * i / steps; p.push((i ? 'L' : 'M') + r1(px) + ' ' + r1(up(px))); }
    for (i = steps; i >= 0; i--) { px = x + w * i / steps; p.push('L' + r1(px) + ' ' + r1(lo(px))); }
    return p.join(' ') + 'Z';
  }

  function foamPath(x, y, w, h, dip) {
    var p = ['M' + r1(x) + ' ' + r1(y + h)], i, steps = 24, px, py;
    for (i = 0; i <= steps; i++) {
      px = x + w * i / steps;
      py = y + (dip ? dip(px) : 0);
      p.push('L' + r1(px) + ' ' + r1(py));
    }
    p.push('L' + r1(x + w) + ' ' + r1(y + h) + 'Z');
    return p.join(' ');
  }

  function maxH(data) {
    var m = 0, k;
    for (k in data.beds) if (data.beds[k].h > m) m = data.beds[k].h;
    return m || 16;
  }

  function svg(data, idA, idB, narrow, compact) {
    var g = G(narrow), A = data.beds[idA], B = data.beds[idB];
    if (!A || !B) return '';
    var mh = maxH(data), vy = Math.floor(g.floor - mh * g.s - (narrow ? 54 : 40)), H = g.cy + g.ch + 26 - vy, tag = narrow ? 'n' : 'w';
    var o = ['<svg class="ck ' + (narrow ? 'ck--narrow' : 'ck--wide') + (compact ? ' ck--still' : '') + '" viewBox="0 ' + vy + ' ' + g.W + ' ' + H + '" width="' + g.W + '" height="' + H + '" role="img" aria-label="' + esc(data.a + ' ' + A.name + ', ' + num(A.h) + ' inches, beside ' + data.b + ' ' + B.name + ', ' + num(B.h) + ' inches, cut open to scale') + '" data-pair="' + esc(idA + ' ' + idB) + '">'];
    o.push('<defs><pattern id="ckh-' + tag + '" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="6" class="ck-hl"/></pattern>' +
      '<pattern id="ckd-' + tag + '" width="7" height="7" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r=".8" class="ck-dl"/><circle cx="5.5" cy="5.5" r=".6" class="ck-dl"/></pattern>' +
      '<pattern id="ckz-' + tag + '" width="10" height="10" patternUnits="userSpaceOnUse"><line x1="5" y1="0" x2="5" y2="10" class="ck-zl"/></pattern></defs>');
    o.push('<g class="ck-fill" style="--h:url(#ckh-' + tag + ');--d:url(#ckd-' + tag + ');--z:url(#ckz-' + tag + ')">');
    o.push(ruler(g, mh));
    var a = bedSvg(A, g.ax, g, 'a', data.a), b = bedSvg(B, g.bx, g, 'b', data.b);
    o.push(a.svg, b.svg, brackets(A, a.b, g.ax, g, 'a'), brackets(B, b.b, g.bx, g, 'b'));
    o.push(closeup(A, g, 'a', compact), closeup(B, g, 'b', compact));
    o.push('<text class="ck-cul" x="' + g.gx + '" y="' + (g.cy + g.ch + 18) + '" text-anchor="middle">' + (narrow ? 'Support, close up. Not to scale.' : 'The support under each, close up. Not to scale.') + '</text>');
    o.push('</g></svg>');
    return o.join('');
  }

  var api = { svg: svg, G: G, zig: zig, helix: helix, foamPath: foamPath, bandPath: bandPath, cuGeom: cuGeom };
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; return; }
  window.MOCmpCut = api;

  /* ------------------------------------------------------------------ the page */
  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  function boot(fig) {
    var stage = fig.querySelector('[data-cmp-stage]');
    var dataEl = fig.querySelector('[data-cmp-data]');
    if (!stage || !dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var root = fig.closest('[data-mo-cmp]');
    var pair = { a: stage.getAttribute('data-a'), b: stage.getAttribute('data-b') };
    var at = 0.5, atGoal = 0.5;                      // where the load sits along the close-up (0..1), the same spot on both beds
    function weight(S, i) {
      var cx = S.x + S.pitch * i + S.pitch / 2, px = S.x + at * S.g.w, d = Math.abs(cx - px) / S.pitch;
      return Math.exp(-(d / 0.5) * (d / 0.5));       // pocketed coils: only the coils under the load move
    }
    var narrow = null, sides = null, load = 0, target = 0, held = 0, raf = 0, visible = true, holdT = 0, lastT = 0;

    function isNarrow() { return stage.clientWidth < 520; }

    function render(force) {
      var nw = isNarrow();
      if (!force && nw === narrow && stage.getAttribute('data-drawn') === pair.a + ' ' + pair.b) return;
      narrow = nw;
      // keep the server still while it already shows this pair at this width
      stage.innerHTML = svg(data, pair.a, pair.b, nw);
      stage.querySelectorAll('svg.ck').forEach(function (s) { s.style.display = (s.classList.contains(nw ? 'ck--narrow' : 'ck--wide')) ? '' : 'none'; });
      stage.setAttribute('data-drawn', pair.a + ' ' + pair.b);
      sides = ['a', 'b'].map(function (sd) { return setup(sd); });
      draw();
    }

    function setup(sd) {
      var gEl = stage.querySelector('svg.ck' + (narrow ? '.ck--narrow' : '.ck--wide') + ' [data-cu="' + sd + '"]');
      if (!gEl) return null;
      var g = G(narrow), c = cuGeom(g, sd);
      var cols = [].map.call(gEl.querySelectorAll('[data-cu-col]'), function (el, i) {
        return { el: el, cx: c.x + c.pitch * i + c.pitch / 2, v: 0, c: 0 };
      });
      return { core: gEl.getAttribute('data-core'), g: g, c: c, x: c.x, y0: c.y0, h: c.h, pitch: c.pitch, cols: cols,
        top: gEl.querySelector('[data-cu-top]'), foam: gEl.querySelector('[data-cu-foam]'), zones: gEl.querySelectorAll('[data-cu-zone]'),
        puck: gEl.querySelector('[data-puck]'), dip: 0, dv: 0 };
    }

    // compression (0..1 of the coil's free height) a column settles at under load F
    function settle(core, F) {
      if (core === 'coil-in-coil') {
        var gap = 0.3, k1 = 1.9, k2 = 4.2;             // the outer coil alone first, then outer and inner together
        var c = F / k1;
        if (c > gap) c = gap + (F - gap * k1) / (k1 + k2);
        return Math.min(c, 0.7);
      }
      // Sealy PrecisionFit: "immediate support that grows stronger as the coil is compressed"
      if (core === 'pocket-max' || core === 'pocket') return Math.min((Math.sqrt(1 + 8 * F / 2.0) - 1) / 4, 0.7);
      return Math.min(F / 2.1, 0.7);
    }

    function draw() {
      if (!sides) return;
      sides.forEach(function (S) {
        if (!S) return;
        var free = S.c.bot - S.c.top, mid = S.x + S.g.w / 2, rad = 12;
        if (S.core === 'foam-zone') {
          var D = S.dip * (S.h - 10), sig = S.pitch * 1.05;
          mid = S.x + at * S.g.w;
          // zones 2 and 4 are the firmer ones (Serta): they give a little less at the bowl's shoulders
          var dipF = function (px) {
            var dx = px - mid, f = (px - S.x) / S.g.w;
            var m = 1 - 0.28 * Math.exp(-Math.pow((f - 0.3) / 0.09, 2)) - 0.28 * Math.exp(-Math.pow((f - 0.7) / 0.09, 2));
            return D * m * Math.exp(-dx * dx / (2 * sig * sig));
          };
          S.foam.setAttribute('d', foamPath(S.x, S.y0 + 10, S.g.w, S.h - 10, dipF));
          for (var zi = 0; zi < S.zones.length; zi++) {
            S.zones[zi].setAttribute('d', bandPath(S.x + S.g.w * zi / 5, S.g.w / 5, function (px) { return S.y0 + 10 + dipF(px); }, function (px) { return S.y0 + 24 + dipF(px) * 0.72; }));
          }
          S.top.setAttribute('d', bandPath(S.x, S.g.w, function (px) { return S.y0 + dipF(px); }, function (px) { return S.y0 + 10 + dipF(px); }));
          S.puck.setAttribute('cy', r1(S.y0 - rad - 1 + D));
          S.puck.setAttribute('cx', r1(mid));
          return;
        }
        var inTop0 = S.c.top + free * 0.3, cols = S.cols;
        cols.forEach(function (col) {
          if (col.drawn != null && Math.abs(col.c - col.drawn) < 0.0005) return;
          col.drawn = col.c;
          var top = S.c.top + col.c * free;
          var d = coilDs(S.core, col.cx, top, S.c.bot, S.pitch, inTop0);
          if (!col.ob) { col.ob = col.el.querySelectorAll('[data-ob]'); col.of = col.el.querySelectorAll('[data-of]'); col.ib = col.el.querySelector('[data-inb]'); col.if_ = col.el.querySelector('[data-in]'); }
          d.out.forEach(function (pp, j) { if (col.ob[j]) { col.ob[j].setAttribute('d', pp[0]); col.of[j].setAttribute('d', pp[1]); } });
          if (d.inn && col.ib) {
            col.ib.setAttribute('d', d.inn[0]); col.if_.setAttribute('d', d.inn[1]);
            var on = top > inTop0 + 0.5 && load > 0.05;
            col.ib.classList.toggle('is-on', on); col.if_.classList.toggle('is-on', on);
          }
        });
        // the comfort strip rests on the coil tops: eased between column centres
        var dyAt = function (px) {
          var f = (px - cols[0].cx) / S.pitch, i = Math.floor(f), t = f - i;
          if (f <= 0) return cols[0].c * free;
          if (i >= cols.length - 1) return cols[cols.length - 1].c * free;
          var e = (1 - Math.cos(t * Math.PI)) / 2;
          return (cols[i].c * (1 - e) + cols[i + 1].c * e) * free;
        };
        S.top.setAttribute('d', bandPath(S.x, S.g.w, function (px) { return S.y0 + dyAt(px); }, function (px) { return S.y0 + 10 + dyAt(px); }));
        mid = S.x + at * S.g.w;
        S.puck.setAttribute('cy', r1(S.y0 - rad - 1 + dyAt(mid)));
        S.puck.setAttribute('cx', r1(mid));
      });
    }

    function step(t) {
      raf = 0;
      var dt = Math.min(0.033, lastT ? (t - lastT) / 1000 : 0.016);
      lastT = t;
      if (held) target = Math.min(1.75, 1 + (t - holdT) / 1400);
      load += (target - load) * Math.min(1, dt * 7);
      at += (atGoal - at) * Math.min(1, dt * 12);
      var moving = Math.abs(target - load) > 0.002 || Math.abs(atGoal - at) > 0.001;
      (sides || []).forEach(function (S) {
        if (!S) return;
        if (S.core === 'foam-zone') {
          var goal = Math.min(load / 2.6, 0.55);
          var a = (goal - S.dip) * 180 - S.dv * 22;
          S.dv += a * dt; S.dip += S.dv * dt;
          if (Math.abs(S.dv) > 0.002 || Math.abs(goal - S.dip) > 0.002) moving = true;
          return;
        }
        S.cols.forEach(function (c, i) {
          var w = weight(S, i);
          var goal = settle(S.core, load * w);
          var a = (goal - c.c) * 210 - c.v * 20;
          c.v += a * dt; c.c += c.v * dt;
          if (c.c < 0) { c.c = 0; c.v = 0; }
          if (Math.abs(c.v) > 0.002 || Math.abs(goal - c.c) > 0.002) moving = true;
        });
      });
      draw();
      if ((moving || held) && visible) raf = requestAnimationFrame(step);
      else lastT = 0;
    }

    function kick() { if (!raf && visible && !reduce) { lastT = 0; raf = requestAnimationFrame(step); } }

    function snap() {   // reduced motion: jump to the end state
      (sides || []).forEach(function (S) {
        if (!S) return;
        if (S.core === 'foam-zone') { S.dip = Math.min(load / 2.6, 0.55); S.dv = 0; return; }
        S.cols.forEach(function (c, i) { c.c = settle(S.core, load * weight(S, i)); c.v = 0; });
      });
      draw();
    }

    function press(on) {
      if (on) { held = 1; holdT = performance.now(); target = 1; }
      else { held = 0; target = 0; }
      fig.classList.toggle('is-pressing', !!on);
      if (reduce) { load = on ? 1 : 0; snap(); } else kick();
    }

    var btn = fig.querySelector('[data-cmp-press]');
    if (btn) {
      btn.addEventListener('pointerdown', function (e) { if (e.button === 0) { btn.setPointerCapture && btn.setPointerCapture(e.pointerId); press(true); } });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(function (ev) { btn.addEventListener(ev, function () { if (held) press(false); }); });
      btn.addEventListener('keydown', function (e) { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); press(true); } });
      btn.addEventListener('keyup', function (e) { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); press(false); } });
      btn.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        atGoal = Math.max(0.1, Math.min(0.9, atGoal + (e.key === 'ArrowLeft' ? -0.2 : 0.2)));
        if (reduce) { at = atGoal; snap(); } else kick();
      });
      btn.addEventListener('click', function (e) { if (e.detail === 0 && !held) { press(true); setTimeout(function () { press(false); }, 700); } });
    }
    function aim(e) {
      var sv = stage.querySelector('svg.ck'), m = sv && sv.getScreenCTM && sv.getScreenCTM();
      if (!m || !sides) return;
      var x = (e.clientX - m.e) / m.a, f = null;
      sides.forEach(function (S) { if (S && x >= S.x - 10 && x <= S.x + S.g.w + 10) f = (x - S.x) / S.g.w; });
      if (f != null) { atGoal = Math.max(0.1, Math.min(0.9, f)); if (reduce) at = atGoal; }
    }
    stage.addEventListener('pointerdown', function (e) { if (e.button === 0) { stage.setPointerCapture && stage.setPointerCapture(e.pointerId); aim(e); press(true); } });
    stage.addEventListener('pointermove', function (e) { if (held) { aim(e); if (reduce) snap(); else kick(); } });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(function (ev) { stage.addEventListener(ev, function () { if (held) press(false); }); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        visible = es[0].isIntersecting;
        if (visible) { kick(); if (!fig.classList.contains('is-shown')) intro(); }
      }, { threshold: 0.25 }).observe(fig);
    }

    // one orchestrated moment on first view: the layers stack up (CSS), then one press and release
    function intro() {
      fig.classList.add('is-shown');
      if (reduce) return;
      if (fig.classList.contains('is-armed')) fig.classList.add('is-built');
      setTimeout(function () { if (!held) { target = 1; kick(); setTimeout(function () { if (!held) { target = 0; kick(); } }, 900); } }, 900);
    }

    // the panel sticks under the site header; when the header is not sticky (print, slice captures) neither is the panel
    var hdr = document.querySelector('.shopify-section-group-header-group'), stT = 0;
    function stickCheck() {
      stT = 0;
      if (!hdr) return;
      var p = getComputedStyle(hdr).position, un = !(p === 'sticky' || p === 'fixed');
      var host = root || fig;
      if (un !== host.classList.contains('is-unstuck')) host.classList.toggle('is-unstuck', un);
    }
    window.addEventListener('scroll', function () { if (!stT) stT = setTimeout(stickCheck, 300); }, { passive: true });
    stickCheck();

    var rz = 0;
    window.addEventListener('resize', function () { clearTimeout(rz); rz = setTimeout(function () { render(false); }, 120); });

    if (root) root.addEventListener('mo-cmp:pair', function (e) {
      pair = { a: e.detail.a, b: e.detail.b };
      stage.setAttribute('data-a', pair.a); stage.setAttribute('data-b', pair.b);
      render(true);
      fig.querySelectorAll('[data-cmp-key]').forEach(function (k) {
        var id = k.getAttribute('data-cmp-key');
        k.hidden = !(id === pair.a || id === pair.b);
      });
      if (!reduce) { fig.classList.remove('is-built'); fig.classList.add('is-armed'); void fig.offsetWidth; fig.classList.add('is-built'); }
    });

    render(false);
    var r0 = fig.getBoundingClientRect();
    if (!reduce && r0.top > innerHeight * 0.8) fig.classList.add('is-armed');
    fig.classList.add('is-live');
  }

  function init() { document.querySelectorAll('[data-cmp-cut]').forEach(boot); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
